/**
 * A KYC capture in progress, kept where a page reload cannot reach it.
 *
 * Photographing both sides of a card and taking a selfie is two or three
 * minutes of a guest's life, and until now every one of those minutes was
 * held in React state alone. A tab discarded by iOS under memory pressure, a
 * pull-to-refresh, a phone call that backgrounds the browser long enough for
 * it to reload the page - any of them threw the whole thing away and put the
 * guest back on step one with the camera warming up again. People do not do
 * that twice; they abandon.
 *
 * So the flow writes itself down as it goes. IndexedDB, not localStorage,
 * because the things worth saving are three photographs: several megabytes of
 * Blob that localStorage cannot hold at all (5MB of *string*, and base64
 * inflates by a third before you start).
 *
 * Everything here fails soft. Private-mode Safari, a browser with storage
 * disabled, a quota that will not stretch to another photo - none of that is
 * allowed to break the capture flow, because a guest who cannot be *resumed*
 * can still verify perfectly well in one sitting. Every function resolves;
 * none of them throw.
 */

const DB_NAME = 'cloudbeddy-kyc';
const STORE = 'drafts';
const DB_VERSION = 1;

/**
 * How long a half-finished verification is worth resuming.
 *
 * Long enough to survive a reload, a lost signal or a walk to reception with
 * the card; short enough that a shared or public device is not still holding
 * photographs of somebody's Aadhaar tomorrow morning. The photos are the most
 * sensitive thing this app ever touches, so the clock is deliberately tight.
 */
const TTL_MS = 90 * 60 * 1000;   // 90 minutes

const hasIDB = () => {
  try {
    return typeof indexedDB !== 'undefined' && indexedDB !== null;
  } catch {
    return false;   // some hardened browsers throw on the property itself
  }
};

let dbPromise = null;

function openDB() {
  if (!hasIDB()) return Promise.resolve(null);
  if (dbPromise) return dbPromise;
  dbPromise = new Promise((resolve) => {
    let req;
    try {
      req = indexedDB.open(DB_NAME, DB_VERSION);
    } catch {
      resolve(null);
      return;
    }
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(STORE)) db.createObjectStore(STORE);
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => resolve(null);
    req.onblocked = () => resolve(null);
    // A request that never fires either handler (it happens, on browsers that
    // prompt for storage permission and are then left alone) must not hang the
    // modal's first paint behind it.
    setTimeout(() => resolve(req.readyState === 'done' ? req.result || null : null), 2500);
  }).catch(() => null);
  return dbPromise;
}

function tx(db, mode, fn) {
  return new Promise((resolve) => {
    let t;
    try {
      t = db.transaction(STORE, mode);
    } catch {
      resolve(null);
      return;
    }
    let out = null;
    const req = fn(t.objectStore(STORE));
    if (req) req.onsuccess = () => { out = req.result; };
    t.oncomplete = () => resolve(out);
    t.onerror = () => resolve(null);
    t.onabort = () => resolve(null);
  }).catch(() => null);
}

/**
 * A File as IndexedDB will reliably give it back.
 *
 * Blobs round-trip everywhere; File does not - older WebKit hands back a
 * plain Blob with the name and type stripped, and the name is what tells the
 * backend's multipart parser it received `front.jpg` rather than `blob`. So
 * we take the File apart on the way in and rebuild it on the way out.
 */
function packFile(file) {
  if (!file) return null;
  return {
    blob: file.slice(0, file.size, file.type || 'image/jpeg'),
    name: file.name || 'photo.jpg',
    type: file.type || 'image/jpeg',
  };
}

function unpackFile(packed) {
  if (!packed || !packed.blob) return null;
  try {
    return new File([packed.blob], packed.name || 'photo.jpg',
      { type: packed.type || 'image/jpeg' });
  } catch {
    // No File constructor (very old Edge): the Blob alone still uploads, it
    // just arrives with a generic filename.
    return packed.blob;
  }
}

/**
 * Save the draft, replacing whatever was there.
 *
 * `fields` is any JSON-able bag of form state; `files` maps a slot name to a
 * File or null. Photos are only rewritten when they actually changed, so
 * typing a digit into the ID number field does not re-serialise six megabytes
 * of camera output on every keystroke.
 */
export async function saveDraft(key, { fields = {}, files = {} } = {}) {
  const db = await openDB();
  if (!db) return false;
  const packed = {};
  for (const [slot, file] of Object.entries(files)) {
    packed[slot] = packFile(file);
  }
  const ok = await tx(db, 'readwrite', (store) =>
    store.put({ savedAt: Date.now(), fields, files: packed }, key));
  return ok !== null;
}

/**
 * The saved draft, or null when there is nothing worth resuming.
 *
 * An expired draft is not just ignored, it is deleted - leaving photographs of
 * an ID on the device past their welcome is the one failure mode here with a
 * real cost attached.
 */
export async function loadDraft(key) {
  const db = await openDB();
  if (!db) return null;
  const row = await tx(db, 'readonly', (store) => store.get(key));
  if (!row) return null;
  if (!row.savedAt || Date.now() - row.savedAt > TTL_MS) {
    clearDraft(key);
    return null;
  }
  const files = {};
  for (const [slot, packed] of Object.entries(row.files || {})) {
    files[slot] = unpackFile(packed);
  }
  return { fields: row.fields || {}, files, savedAt: row.savedAt };
}

export async function clearDraft(key) {
  const db = await openDB();
  if (!db) return false;
  await tx(db, 'readwrite', (store) => store.delete(key));
  return true;
}

/**
 * Drop every draft that has aged out.
 *
 * Called when the modal opens rather than on a timer: the only moment we are
 * certain the app is awake and the user is present, and the only moment the
 * cost of a sweep is hidden behind a camera starting up.
 */
export async function purgeExpiredDrafts() {
  const db = await openDB();
  if (!db) return;
  const cutoff = Date.now() - TTL_MS;
  await tx(db, 'readwrite', (store) => {
    const req = store.openCursor();
    req.onsuccess = () => {
      const cursor = req.result;
      if (!cursor) return;
      const row = cursor.value;
      if (!row || !row.savedAt || row.savedAt < cutoff) cursor.delete();
      cursor.continue();
    };
    return null;
  });
}

/**
 * Stable per-flow key: the same guest resuming the same link finds their work.
 *
 * `scope` is what keeps that "the same guest" honest on a shared device. The
 * link flows are already unique per person because the one-time token is part
 * of the endpoint, but the signed-in flow posts to a fixed URL - so without an
 * account id in the key, the front desk's tablet would offer the next guest
 * the previous one's half-finished Aadhaar photos.
 */
export function draftKeyFor(endpoint, bookingId, scope) {
  return `kyc:${scope || 'anon'}:${endpoint || 'default'}:${bookingId || '-'}`;
}

/**
 * Wipe every saved draft.
 *
 * Called on logout. Whoever picks the device up next is a different person as
 * far as we can tell, and photographs of someone's ID are not something to
 * leave lying about for them to resume into.
 */
export async function clearAllDrafts() {
  const db = await openDB();
  if (!db) return;
  await tx(db, 'readwrite', (store) => store.clear());
  try {
    Object.keys(sessionStorage)
      .filter(k => k.startsWith('cloudbeddy:kyc-link:'))
      .forEach(k => sessionStorage.removeItem(k));
  } catch { /* storage disabled */ }
}

/** "3 minutes ago", for the resume banner. Vague on purpose; precision here is noise. */
export function describeAge(savedAt) {
  if (!savedAt) return '';
  const mins = Math.floor((Date.now() - savedAt) / 60000);
  if (mins < 1) return 'a moment ago';
  if (mins === 1) return 'a minute ago';
  if (mins < 60) return `${mins} minutes ago`;
  return 'earlier';
}

/* ------------------------------------------------------------------
 * Where the guest had got to on a shareable verification link.
 * ------------------------------------------------------------------
 * The KYC draft above rescues what is inside the capture modal. It cannot
 * rescue the screen the modal was opened FROM - and on a guest link that
 * screen is an OTP gate. Reload the page and the React state saying "this
 * mobile has been confirmed" is gone, so a guest who was three photos deep
 * came back to a page asking them to start with the OTP again. The draft was
 * still there; they just had no route back to it.
 *
 * This is a UI convenience and nothing more. The proof that a number cleared
 * its OTP lives on the SERVER, keyed to the link (guest_link_otp.remember),
 * and every KYC endpoint re-checks it - a forged entry here buys a 428 and
 * not one byte of anybody's ID. sessionStorage, so it dies with the tab: a
 * reload keeps it, handing the phone to the next person at the desk does not.
 */

const LINK_TTL_MS = TTL_MS;
const linkKey = (kind, token) => `cloudbeddy:kyc-link:${kind}:${token}`;

export function saveLinkStage(kind, token, stage) {
  if (!token) return;
  try {
    sessionStorage.setItem(linkKey(kind, token),
      JSON.stringify({ savedAt: Date.now(), stage }));
  } catch { /* storage disabled or full - the flow just will not resume */ }
}

export function loadLinkStage(kind, token) {
  if (!token) return null;
  try {
    const raw = sessionStorage.getItem(linkKey(kind, token));
    if (!raw) return null;
    const row = JSON.parse(raw);
    if (!row?.savedAt || Date.now() - row.savedAt > LINK_TTL_MS) {
      clearLinkStage(kind, token);
      return null;
    }
    return row.stage || null;
  } catch {
    return null;
  }
}

export function clearLinkStage(kind, token) {
  if (!token) return;
  try {
    sessionStorage.removeItem(linkKey(kind, token));
  } catch { /* nothing to do */ }
}
